import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Brandmark, Wordmark } from "@/components/Brand";
import { toast } from "@/hooks/use-toast";

/**
 * /reset-password — user lands here after clicking the recovery email.
 *
 * By the time this page renders, `/auth/callback` has already exchanged
 * the `?code=...` in the URL for a real session (a short-lived recovery
 * session). That means supabase.auth.updateUser() will succeed if — and
 * only if — we have an active session. If the user opens /reset-password
 * directly without going through the email link, we bounce them to
 * /forgot-password so they can request a new link.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  // Verify we have a recovery session before letting the user set a new
  // password. If not, punt back to /forgot-password.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (!session) {
        toast({
          title: "Reset link expired",
          description: "That password reset link is no longer valid. Please request a new one.",
          variant: "destructive",
        });
        navigate("/forgot-password", { replace: true });
        return;
      }
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Use at least 8 characters — a passphrase works well.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({
        title: "Passwords don't match",
        description: "Please type the same password in both fields.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      toast({ title: "Couldn't update password", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Password updated", description: "You're signed in with your new password." });
    navigate("/", { replace: true });
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto flex min-h-screen max-w-[1080px] flex-col items-center justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <Brandmark size={32} />
          <Wordmark className="text-[24px]" />
        </div>

        <div className="w-full max-w-[420px] rounded-xl border border-line bg-canvas p-8 shadow-[0_1px_2px_rgba(26,25,21,.04)]">
          <h1 className="font-serif text-[28px] leading-[1.1] text-ink">
            Set a new <em className="italic text-accent-rf">password</em>
          </h1>
          <p className="mt-2 text-[13px] text-ink-3">
            Pick something you'll remember. Aim for 12+ characters, mix in a number or symbol.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-3">
            <label className="block">
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-ink-3">New password</div>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="auth-input"
                autoComplete="new-password"
                disabled={!ready}
              />
            </label>

            <label className="block">
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-ink-3">Confirm password</div>
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="auth-input"
                autoComplete="new-password"
                disabled={!ready}
              />
            </label>

            <button
              type="submit"
              disabled={submitting || !ready}
              className="mt-2 w-full rounded-md bg-ink px-4 py-2.5 text-[13px] font-medium text-canvas transition-colors duration-[120ms] hover:bg-ink/90 disabled:opacity-50"
            >
              {submitting ? "Saving…" : "Update password"}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .auth-input {
          width: 100%;
          border-radius: 6px;
          border: 1px solid hsl(var(--line));
          background: hsl(var(--canvas));
          padding: 9px 12px;
          font-size: 13px;
          color: hsl(var(--ink));
          transition: border-color 120ms;
        }
        .auth-input::placeholder { color: hsl(var(--ink-3)); }
        .auth-input:focus { outline: none; border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}
